import { registerFormProtection } from "./form";

type RecaptchaWidgetId = number | string;

type RecaptchaApi = {
  ready(callback: () => void): void;
  render(
    container: HTMLElement,
    options: {
      sitekey: string;
      size: "invisible";
      callback: () => void;
      "error-callback": () => void;
      "expired-callback": () => void;
    },
  ): RecaptchaWidgetId;
  execute(widgetIdOrSiteKey: RecaptchaWidgetId, options?: { action: string }): Promise<string> | void;
  reset(widgetId?: RecaptchaWidgetId): void;
};

type InvisibleWidget = {
  id: RecaptchaWidgetId;
  pending?: {
    resolve: () => void;
    reject: (error: Error) => void;
  };
};

const invisibleWidgets = new WeakMap<HTMLFormElement, InvisibleWidget>();

declare global {
  interface Window {
    grecaptcha?: RecaptchaApi;
  }
}

function waitForRecaptcha(timeoutMs = 10000): Promise<RecaptchaApi> {
  return new Promise((resolve, reject) => {
    const startedAt = Date.now();
    const wait = () => {
      const recaptcha = window.grecaptcha;
      if (recaptcha && typeof recaptcha.ready === "function") {
        recaptcha.ready(() => resolve(recaptcha));
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        reject(new Error("reCAPTCHA konnte nicht geladen werden. Bitte lade die Seite neu."));
        return;
      }

      window.setTimeout(wait, 50);
    };

    wait();
  });
}

function isBypassed(form: HTMLFormElement): boolean {
  return form.dataset.captchaBypass === "true" || form.dataset.captchaBypass === "1";
}

function rejectPending(widget: InvisibleWidget, message: string): void {
  widget.pending?.reject(new Error(message));
  widget.pending = undefined;
}

async function getInvisibleWidget(form: HTMLFormElement): Promise<InvisibleWidget> {
  const existing = invisibleWidgets.get(form);
  if (existing) {
    return existing;
  }

  const siteKey = form.dataset.captchaSitekey;
  const container = form.querySelector<HTMLElement>("[data-captcha-container]");
  if (!siteKey || !container) {
    throw new Error("reCAPTCHA konnte nicht vorbereitet werden. Bitte lade die Seite neu.");
  }

  const recaptcha = await waitForRecaptcha();
  const widget = {} as InvisibleWidget;
  widget.id = recaptcha.render(container, {
    sitekey: siteKey,
    size: "invisible",
    callback: () => {
      widget.pending?.resolve();
      widget.pending = undefined;
    },
    "error-callback": () => rejectPending(widget, "reCAPTCHA konnte nicht bestätigt werden. Bitte versuche es erneut."),
    "expired-callback": () => rejectPending(widget, "reCAPTCHA ist abgelaufen. Bitte versuche es erneut."),
  });
  invisibleWidgets.set(form, widget);
  return widget;
}

async function verifyInvisibleRecaptcha(form: HTMLFormElement): Promise<void> {
  if (isBypassed(form)) {
    return;
  }

  const widget = await getInvisibleWidget(form);
  if (widget.pending) {
    throw new Error("Die reCAPTCHA-Prüfung läuft bereits.");
  }

  const recaptcha = await waitForRecaptcha();
  await new Promise<void>((resolve, reject) => {
    widget.pending = { resolve, reject };
    try {
      const result = recaptcha.execute(widget.id);
      if (result instanceof Promise) {
        void result.catch(() => rejectPending(widget, "reCAPTCHA konnte nicht bestätigt werden. Bitte versuche es erneut."));
      }
    } catch {
      rejectPending(widget, "reCAPTCHA konnte nicht bestätigt werden. Bitte versuche es erneut.");
    }
  });

  // Invisible v2 writes its verification value into this field inside the
  // form. Keeping this explicit makes the subsequent native or AJAX request
  // fail closed if Google did not provide a value.
  const responseField = form.querySelector<HTMLInputElement | HTMLTextAreaElement>('[name="g-recaptcha-response"]');
  if (!responseField?.value) {
    resetInvisibleRecaptcha(form);
    throw new Error("reCAPTCHA hat keinen Prüfwert geliefert. Bitte versuche es erneut.");
  }
}

function resetInvisibleRecaptcha(form: HTMLFormElement): void {
  const widget = invisibleWidgets.get(form);
  if (!widget) {
    return;
  }

  rejectPending(widget, "");
  window.grecaptcha?.reset(widget.id);
}

/** Fetch a reCAPTCHA v3 token for integrations that submit their own payload. */
export async function executeRecaptchaV3(siteKey: string, action: string): Promise<string> {
  const recaptcha = await waitForRecaptcha();
  const token = await recaptcha.execute(siteKey, { action });
  if (typeof token !== "string" || !token) {
    throw new Error("reCAPTCHA hat kein Token geliefert.");
  }
  return token;
}

// The form controller does not know which CAPTCHA provider is in use. This
// adapter is selected only by `data-form-protection="recaptcha-v2"`.
registerFormProtection("recaptcha-v2", {
  prepare: verifyInvisibleRecaptcha,
  reset: resetInvisibleRecaptcha,
});
